import React, {useEffect, useState} from "react";
import {Button} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import EmailIcon from "@material-ui/icons/Email";
import {NotificationManager} from "react-notifications";
import ActionButton from "../form/input/ActionButton";
import AutocompleteInput from "../form/input/AutocompleteInput";
import Popup from "../presentation/Popup";
import UserService from "../../services/user.service";
import {exportPdfDataUriWithEmbeddedImages} from "../../helpers/pdfExport";

const emailRegex = /^\S+@\S+\.\S+$/;

const useStyles = makeStyles((theme) => ({
    fields: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    messagePreview: {
        marginTop: theme.spacing(3),
        marginBottom: 0,
        padding: theme.spacing(2),
        background: "#ffffff",
        border: "1px solid rgba(176, 141, 87, 0.18)",
        borderRadius: 10,
        lineHeight: 1.55,
    },
}));

export default function GeneratedDocumentEmailAction({pdfRef, funeral, user}) {
    const [open, setOpen] = useState(false);
    const [emails, setEmails] = useState(funeral?.ca_email ? [funeral.ca_email] : []);
    const [contacts, setContacts] = useState([]);
    const [contactGroups, setContactGroups] = useState([]);
    const [groupInput, setGroupInput] = useState("");
    const [emailInput, setEmailInput] = useState("");
    const [sending, setSending] = useState(false);
    const classes = useStyles();

    const getFileName = () => {
        const fileName = pdfRef.current?.props?.fileName || "dokument";
        return fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;
    };

    const getDocumentName = () => (pdfRef.current?.props?.fileName || "dokument").replace(/\.pdf$/i, "");

    useEffect(() => {
        UserService.getContacts(0).then(
            (response) => setContacts(response.data),
            () => setContacts([])
        );
        UserService.getContactGroups().then(
            (response) => setContactGroups(response.data),
            () => setContactGroups([])
        );
    }, []);

    const contactOptions = contacts.map((contact) => {
        const name = contact.name || "Kontakt";
        const email = (contact.email || "").trim();
        return {
            title: email ? `${name} — ${email}` : `${name} — Nema email`,
            searchText: `${contact.name || ""} ${contact.email || ""} ${contact.phone || ""}`.toLowerCase(),
            email,
            disabled: !email
        };
    });

    const groupOptions = contactGroups.map((group) => {
        const groupEmails = (group.contacts || [])
            .map((contact) => (contact?.email || "").trim())
            .filter(Boolean);
        return {
            title: `${group.name || "Skupina"} — ${groupEmails.length} emailov`,
            searchText: `${group.name || ""} ${(group.contacts || []).map((contact) => `${contact?.name || ""} ${contact?.email || ""} ${contact?.phone || ""}`).join(" ")}`.toLowerCase(),
            emails: groupEmails,
            disabled: groupEmails.length === 0
        };
    });

    const filterContactOptions = (options, state) => {
        const search = state.inputValue.trim().toLowerCase();
        const selectedEmails = normalizeEmails(emails);
        return options.filter((option) => (
            !selectedEmails.includes(option.email) && (!search || option.searchText.includes(search))
        ));
    };

    const filterGroupOptions = (options, state) => {
        const search = state.inputValue.trim().toLowerCase();
        if (!search) return options;
        return options.filter((option) => option.searchText.includes(search));
    };

    const normalizeEmails = (values) => [...new Set(values.flatMap((value) => (
        typeof value === "string" ? value : value.email
    )).flatMap((value) => (value || "").split(/[;,\s]+/)).map((value) => value.trim()).filter(Boolean))];

    const commitEmailInput = () => {
        const pendingEmail = emailInput.trim();
        if (!pendingEmail) return normalizeEmails(emails);

        const nextEmails = normalizeEmails([...emails, pendingEmail]);
        setEmails(nextEmails);
        setEmailInput("");
        return nextEmails;
    };

    const handleOpen = () => {
        setEmails(funeral?.ca_email ? [funeral.ca_email] : []);
        setGroupInput("");
        setEmailInput("");
        setOpen(true);
    };

    const handleGroupChange = (event, group) => {
        if (!group) return;
        setEmails(normalizeEmails([...emails, ...group.emails]));
        setGroupInput("");
    };

    const handleSend = async () => {
        const recipients = commitEmailInput();

        if (recipients.length === 0 || recipients.some((recipient) => !emailRegex.test(recipient))) {
            NotificationManager.error("Zadajte platnú emailovú adresu.", "Chyba", 5000);
            return;
        }

        setSending(true);
        try {
            const dataUri = await exportPdfDataUriWithEmbeddedImages(pdfRef);
            const pdfBase64 = dataUri.split("base64,")[1];

            await UserService.sendGeneratedDocumentEmail({
                funeral_id: funeral._id,
                emails: recipients,
                documentName: getDocumentName(),
                fileName: getFileName(),
                pdfBase64
            });

            NotificationManager.success("Dokument bol odoslaný emailom.", "Odoslané", 5000);
            setOpen(false);
        } catch (e) {
            NotificationManager.error("Dokument sa nepodarilo odoslať emailom.", "Chyba", 5000);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <ActionButton color="primary">
                <Tooltip title="Odoslať emailom">
                    <EmailIcon fontSize="small" onClick={handleOpen}/>
                </Tooltip>
            </ActionButton>
            <Popup
                title="Odoslať dokument emailom"
                openPopup={open}
                setOpenPopup={setOpen}
                actions={(
                    <Button color="primary" variant="outlined" onClick={handleSend} disabled={sending}>
                        {sending ? "Odosielam..." : "Odoslať"}
                    </Button>
                )}
            >
                <div className={classes.fields}>
                    <AutocompleteInput
                        label="Vyberte skupinu na predvyplnenie"
                        name="contactGroup"
                        value={null}
                        inputValue={groupInput}
                        options={groupOptions}
                        onChange={handleGroupChange}
                        onInputChange={(event, value, reason) => setGroupInput(reason === "reset" ? "" : value)}
                        fullWidth
                        filterOptions={filterGroupOptions}
                        getOptionDisabled={(option) => option.disabled}
                        renderOption={(option) => option.title}
                    />
                    <AutocompleteInput
                        label="Emailové adresy"
                        name="emails"
                        value={emails}
                        inputValue={emailInput}
                        options={contactOptions}
                        onChange={(event, values) => setEmails(normalizeEmails(values))}
                        onInputChange={(event, value, reason) => setEmailInput(reason === "reset" ? "" : value)}
                        onBlur={commitEmailInput}
                        multiple
                        freeSolo
                        fullWidth
                        filterOptions={filterContactOptions}
                        getOptionDisabled={(option) => option.disabled}
                        renderOption={(option) => option.title}
                    />
                </div>
                <p className={classes.messagePreview}>
                    Dobrý deň,<br/><br/>
                    pohrebná služba {user?.name || ""} Vám zasiela tento dokument "{getDocumentName()}".<br/><br/>
                    S pozdravom, Moderné pohrebníctvo.
                </p>
            </Popup>
        </>
    );
}
